import type {
  DynamicConfig,
  StatsigOptions,
  StatsigUser,
} from '@statsig/js-client';
import { captureException } from '@sentry/vue';
import { StatsigClient } from '@statsig/js-client';

import {
  cookie,
  isStenaLineAffiliateInUrlOrCookie,
  isTrivagoAffiliateInUrlOrCookie,
} from '@libs/cookie';
import { getDomain, getScreenSize, log } from '@libs/utils';

import {
  FeaturePrefix,
  getActiveFeatureToggles,
} from '../customFeatureToggles';

import type {
  StatsigExperimentName,
  StatsigMultiExperimentName,
} from './statsigExperiments';
import { FORCE_ALL_EXPERIMENTS_TO_CONTROL_COOKIE } from './statsigExperiments';
import {
  getCustomerOrigin,
  getInvalidCustomerOriginQueryParamValue,
} from './customerOrigin';
import type { CustomerOrigin } from './customerOrigin';
import { isCrawlerUserAgent } from './crawlerCheck';

interface StatsigUserMetadata {
  customerOrigin: CustomerOrigin;
  domain: string;
  isStena: boolean;
  isTrivago: boolean;
  userSource?: string;
}

const STATSIG_ENABLED = true;
let statsigClient: StatsigClient;

const urlParams = new URL(window.location.href).searchParams;
const sdkKey = getDomain.isProduction
  ? import.meta.env.VITE_STATSIG_CLIENT_PROD
  : import.meta.env.VITE_STATSIG_CLIENT_DEV;

export async function initializeStatsig(): Promise<void> {
  if (STATSIG_ENABLED) {
    lazyLoadStatsig();

    await statsigClient.initializeAsync();

    log({ title: 'Statsig', context: ['Initialized'] });
  }
}

export function checkGate(gateName: string): boolean {
  const gate = statsigClient.checkGate(gateName);

  log({ title: 'Statsig', context: ['Gate', { gateName, gate }] });

  return gate;
}

export function getExperiment(
  experimentName: StatsigExperimentName | StatsigMultiExperimentName
): DynamicConfig {
  const experiment = statsigClient.getExperiment(experimentName);

  log({
    title: 'Statsig',
    context: ['Experiment', { experimentName, experiment }],
  });

  return experiment;
}

export function logStatsigEvent(
  eventName: string,
  eventValue?: string | number,
  eventData?: Record<string, string>
): void {
  if (
    cookie.get(FORCE_ALL_EXPERIMENTS_TO_CONTROL_COOKIE)?.toLowerCase() ===
    'true'
  ) {
    return;
  }

  if (shouldSkipStatsigEvent()) {
    return;
  }

  log({
    title: 'Statsig',
    context: ['Event', { eventName, eventValue, eventData }],
  });
  statsigClient?.logEvent(eventName, eventValue, eventData);
}

export async function flushStatsigEvents(): Promise<void> {
  try {
    await statsigClient?.flush();
  } catch (error) {
    log({ title: 'Statsig', context: ['flush failed', error] });
    captureException(error);
  }
}

function shouldSkipStatsigEvent(): boolean {
  const userAgent = typeof navigator !== 'undefined' && navigator.userAgent;

  return (
    getCustomerOrigin(urlParams) === 'unattributed' ||
    (userAgent ? isCrawlerUserAgent(userAgent) === 'true' : false)
  );
}

function lazyLoadStatsig(): void {
  if (STATSIG_ENABLED) {
    if (statsigClient) {
      return;
    }

    statsigClient = createStatsigClient();
  }
}

function createStatsigClient(): StatsigClient {
  const user: StatsigUser = {
    custom: {
      'btf-tp': [
        ...Object.keys(getActiveFeatureToggles(FeaturePrefix.TripPlanner)),
      ],
      screen_width: window.innerWidth.toString(),
      screen_size: getScreenSize(),
      ...getUserMetadata(),
    },
    locale: getDomain.getCurrentDomainSpec().Locale,
  };

  const options: StatsigOptions = {
    environment: {
      tier: getDomain.isProduction ? 'production' : 'development',
    },
  };

  log({ title: 'Statsig', context: ['Loaded', { user, options }] });

  // NOTE:
  // Sembo defines two SDK keys, for dev and production envs.
  // Those can be found in console.statsig.com settings page.
  // StatsigClient consumes SDK key and generates local storage entry:
  // * key: statsig.stable_id.<HASH FROM SDK KEY>
  //  - Sembo dev local storage key: statsig.stable_id.3204534271
  //  - Sembo prod local storage key: statsig.stable_id.3206961030
  //  - key is unique, constant value derived from SDK key
  // * value: xxx-yyy-zzz
  //  - unique user identifier
  return new StatsigClient(sdkKey, user, options);
}

// NOTE: All user metadata must be taken from the URL and not cookie.
// Otherwise due to long cookie expiration time invalid values will be sent as metadata.
function getUserMetadata(): StatsigUserMetadata {
  const affiliateId = urlParams.get('affiliate-id') ?? undefined;
  const customerOrigin = getCustomerOrigin(urlParams);
  const userSource = urlParams.get('utm_medium');

  reportInvalidCustomerOriginQueryParamValue();

  return {
    customerOrigin,
    domain: getDomain.getCurrentDomainSpec().Domain,
    isStena: isStenaLineAffiliateInUrlOrCookie(),
    isTrivago: isTrivagoAffiliateInUrlOrCookie(),
    ...(affiliateId && { affiliateId }),
    ...(userSource && { userSource }),
  };
}

function reportInvalidCustomerOriginQueryParamValue(): void {
  const invalidCustomerOrigin =
    getInvalidCustomerOriginQueryParamValue(urlParams);

  if (!invalidCustomerOrigin || import.meta.env.MODE === 'development') {
    return;
  }

  captureException(new Error('Invalid customer-origin query parameter'), {
    extra: {
      value: {
        customerOrigin: invalidCustomerOrigin,
        href: window.location.href,
        pathname: window.location.pathname,
        search: window.location.search,
        queryParams: getQueryParamsRecord(),
        hasAffiliateId: urlParams.has('affiliate-id'),
        hasAiTripId: urlParams.has('ai-trip-id'),
        hasPublishedTripId: urlParams.has('published-trip-id'),
      },
    },
  });
}

function getQueryParamsRecord(): Record<string, string> {
  const queryParams: Record<string, string> = {};

  urlParams.forEach((value, key) => {
    queryParams[key] = value;
  });

  return queryParams;
}
